<template>
  <SlideIn
    :open="isOpen"
    :lock-scroll="false"
    heading="Size guide"
    @close="isOpen = false"
  >
    <template #content>
      <Accordion show-arrow open-by-default class="sizeguide-accordion">
        <template #header>Product Measurements</template>
        <template #body>
          <p class="accordion-text">
            In order to help find the clothing size that will fit you best, we
            suggest that you take the following measurements of your body with a
            cloth measuring tape and compare them to those provided for each of
            our pieces.
          </p>
          <div class="size-guide">
            <div
              v-for="(size, index) in sizes"
              :key="size.key"
              class="sizeguide__row"
            >
              <div v-if="index == 0" class="sizeguide__cell"></div>
              <template v-if="index == 0">
                <div
                  v-for="measurement in size.measurements"
                  :key="measurement.key"
                  class="sizeguide__cell"
                >
                  <span>{{ measurement.key }}</span>
                </div>
              </template>
            </div>
            <div v-for="size in sizes" :key="size.key" class="sizeguide__row">
              <div class="sizeguide__cell">{{ size.title }}</div>
              <div
                v-for="measurement in size.measurements"
                :key="measurement.key"
                class="sizeguide__cell"
              >
                <span>{{ measurement.value }}</span>
              </div>
            </div>
          </div>
          <img class="diagram" src="@/assets/images/sizing.jpg" />
          <p class="accordion-text">
            Bust - Wrap the tape around the fullest part of your bust and
            shoulder blades, then drop your arms to your sides to measure.
          </p>
          <p class="accordion-text">
            Waist - Keeping the tape flush against the body, measure around your
            natural waistline at the navel.
          </p>
          <p class="accordion-text">
            Hip - Wrap the tape around the fullest part of your hip to measure.
            Inseam measurements vary by style and are noted in the item’s
            description.
          </p>
        </template>
      </Accordion>
      <Accordion show-arrow class="sizeguide-accordion">
        <template #header>Size Conversion</template>
        <template #body>
          <div class="sizeguide">
            <div class="sizeguide__row">
              <div class="sizeguide__cell"></div>
              <div class="sizeguide__cell">UK</div>
              <div class="sizeguide__cell">US</div>
              <div class="sizeguide__cell">AUS</div>
              <div class="sizeguide__cell">EUR</div>
            </div>
            <div class="sizeguide__row">
              <div class="sizeguide__cell">XXS</div>
              <div class="sizeguide__cell">4</div>
              <div class="sizeguide__cell">0</div>
              <div class="sizeguide__cell">4</div>
              <div class="sizeguide__cell">32</div>
            </div>
            <div class="sizeguide__row">
              <div class="sizeguide__cell">XS</div>
              <div class="sizeguide__cell">6</div>
              <div class="sizeguide__cell">2</div>
              <div class="sizeguide__cell">6</div>
              <div class="sizeguide__cell">34</div>
            </div>
            <div class="sizeguide__row">
              <div class="sizeguide__cell">S</div>
              <div class="sizeguide__cell">8</div>
              <div class="sizeguide__cell">4</div>
              <div class="sizeguide__cell">8</div>
              <div class="sizeguide__cell">36</div>
            </div>
            <div class="sizeguide__row">
              <div class="sizeguide__cell">M</div>
              <div class="sizeguide__cell">10</div>
              <div class="sizeguide__cell">6</div>
              <div class="sizeguide__cell">10</div>
              <div class="sizeguide__cell">38</div>
            </div>
            <div class="sizeguide__row">
              <div class="sizeguide__cell">L</div>
              <div class="sizeguide__cell">12</div>
              <div class="sizeguide__cell">8</div>
              <div class="sizeguide__cell">12</div>
              <div class="sizeguide__cell">40</div>
            </div>
            <div class="sizeguide__row">
              <div class="sizeguide__cell">XL</div>
              <div class="sizeguide__cell">14</div>
              <div class="sizeguide__cell">10</div>
              <div class="sizeguide__cell">14</div>
              <div class="sizeguide__cell">42</div>
            </div>
            <div class="sizeguide__row">
              <div class="sizeguide__cell">XXL</div>
              <div class="sizeguide__cell">16</div>
              <div class="sizeguide__cell">12</div>
              <div class="sizeguide__cell">16</div>
              <div class="sizeguide__cell">44</div>
            </div>
          </div>
        </template>
      </Accordion>
    </template>
  </SlideIn>
</template>

<script>
import Accordion from "@/components/Accordion.vue";
import SlideIn from "@/components/SlideIn.vue";
export default {
  name: "SizeGuide",
  components: {
    SlideIn,
    Accordion,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    sizes: {
      type: Array,
      required: true,
    },
  },
  emits: ["update:open"],
  computed: {
    isOpen: {
      get() {
        return this.open;
      },
      set(val) {
        this.$emit("update:open", val);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.sizeguide {
  font-size: 10px;

  overflow-x: scroll;
  overflow-y: hidden;
  white-space: nowrap;

  &__row {
    display: flex;
  }

  &__cell {
    display: flex;
    background-color: $color-white;
    color: $color-dark;
    flex: 1;
    height: 31px;
    justify-content: center;
    align-items: center;
    margin: 2px;
    width: 53px;
    text-transform: capitalize;
    font-size: 10px;
    letter-spacing: 0.55px;
  }
}

.accordion-text {
  font-family: $heading-font-family;
  font-size: 14px;
  font-weight: 300;
  margin: 0 0 20px 0;
  padding: 0 10px;
}

.sizeguide-accordion.accordion--open {
  transition-timing-function: ease-out;
  background-color: $color-light;
  padding-bottom: 10px;
  margin-bottom: 20px;

  .accordion__header {
    transition-timing-function: ease-out;
    padding-top: 20px;
  }
}

.sizeguide-accordion {
  padding: 0;
  margin: 0;
  transition: all 0.3s;
  transition-timing-function: ease-in;

  ::v-deep() {
    .accordion__header {
      transition: padding-top 0.3s;
      transition-timing-function: ease-in;
      align-items: center;
      font-family: $body-font-family;
      text-transform: uppercase;
      width: 100%;
      font-size: 9px;
      background: none;
      outline: none;
      border: none;
      padding: 12px 20px;
      font-weight: 600;
    }
    .accordion__body {
      background-color: $color-light;
      padding: 0px 10px 20px;
    }
  }
}

.diagram {
  display: block;
  margin: 20px auto;
  max-width: 100%;
  width: 100%;
}
</style>
